import React, { useCallback, useEffect, useState } from 'react';
import { statApi } from 'redux/reducers/stat';
import Cookies from 'js-cookie';
import {
  CookieNames,
  CookieStateValues,
  HOURS_TO_REJECT,
  MS_IN_HOUR,
} from 'constants/cookies';
import { cn } from '@bem-react/classname';
import { createPortal } from 'react-dom';
import { useVerifyQuery } from 'generated/graphql';

import './PPPortal.sass';

const getCookiePP = () => {
  const cookieState = Cookies.get(CookieNames.PP_STATE);
  const cookieDate = Cookies.get(CookieNames.PP_DATE);

  return [cookieState, cookieDate];
};

const parseCookieDate = (cookieDate?: string) => (cookieDate
  ? Number(String(cookieDate).trim()) || 0 : 0);

function PPPortal({ text }: { text?: string }) {
  const mcn = cn('PPPortal');
  const [showBanner, setShowBanner] = useState(false);
  const { data, loading: dataLoading } = useVerifyQuery();
  const { useSendStatsMutation, useLazyCheckStatusQuery } = statApi;
  const [sendStats] = useSendStatsMutation();
  const [checkStats, { isLoading: statsLoading }] = useLazyCheckStatusQuery();
  const portalEl = document.getElementById('pp-portal');

  const onPPBtnClick = useCallback((state: boolean) => {
    const cookiesSet = () => {
      Cookies.set(
        CookieNames.PP_STATE,
        state
          ? CookieStateValues.accepted
          : CookieStateValues.rejected,
      );
      Cookies.set(CookieNames.PP_DATE, Date.now().toString());
    };

    setShowBanner(false);

    cookiesSet();

    if (data?.user) {
      sendStats({
        msisdn: data.user.msisdn,
        accepted: state,
        timestamp: Date.now(),
      })
        .then((res) => {
          if ('error' in res) cookiesSet();
        });
    }
  }, [data]);

  const checkWithCookies = useCallback(() => {
    const [cookieState, cookieDate] = getCookiePP();

    if (!cookieState) {
      setShowBanner(true);
      return;
    }

    if (cookieState === CookieStateValues.accepted) {
      setShowBanner(false);
      return;
    }

    if (cookieState === CookieStateValues.rejected) {
      if (!cookieDate) {
        setShowBanner(true);
        return;
      }

      const savedDate = parseCookieDate(cookieDate);
      const hoursPassed = (Date.now() - savedDate) / MS_IN_HOUR;
      setShowBanner(hoursPassed > HOURS_TO_REJECT);
      return;
    }

    setShowBanner(true);
  }, []);

  useEffect(() => {
    if (!data?.user) return;

    const [cookieState, cookieDate] = getCookiePP();

    if (cookieState && cookieDate) {
      sendStats({
        msisdn: data.user.msisdn,
        accepted: Number(cookieState) === 1,
        timestamp: parseCookieDate(cookieDate),
      });
    }
  }, [data]);

  useEffect(() => {
    if (dataLoading) return;

    if (!data?.user) {
      checkWithCookies();

      return;
    }

    checkStats({ msisdn: data.user.msisdn })
      .then((res) => {
        if ('data' in res && res.data) {
          setShowBanner(res.data.show_banner);
        } else {
          checkWithCookies();
        }
      }).catch(() => {
        checkWithCookies();
      });
  }, [data, dataLoading]);

  if (statsLoading || !showBanner || !text) return null;

  return portalEl ? createPortal(
    <div className={mcn('Container')}>
      <div className={mcn('Wrapper')}>
        <div className={mcn('Text')} dangerouslySetInnerHTML={{ __html: text }} />
        <div className={mcn('ButtonsWrapper')}>
          <button
            className={mcn('Button')}
            onClick={() => onPPBtnClick(false)}
            type="button"
          >
            Не принимаю
          </button>
          <button
            className={mcn('Button', { accept: true })}
            onClick={() => onPPBtnClick(true)}
            type="button"
          >
            Принимаю
          </button>
        </div>
      </div>
    </div>,
    portalEl,
  ) : null;
}

PPPortal.defaultProps = {
  text: '',
};

export default PPPortal;
